import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  CircularProgress,
  Alert,
  TablePagination,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import axiosInstance from '../../../../utils/axiosInstance';
import {
  INTERVIEW_STATUSES, INTERVIEW_STATUS_COLORS, INTERVIEW_TYPES,
  formatEnumLabel,
} from '../../../../constants/hrm';
import {
  useInterviews, useCreateInterview, useUpdateInterview,
  useApplicants, useJobs,
} from '../../../../hooks/useHRM';
import { hasPermission } from '../../../../utils/permissionUtils';

const initialFormData = {
  applicant_id: '',
  job_id: '',
  interview_date: '',
  duration_minutes: 60,
  interview_type: 'VIDEO',
  location: '',
  meeting_link: '',
  interviewer_name: '',
};

const feedbackFormInitial = {
  score: '',
  feedback: '',
  recommendation: '',
};

const InterviewList = () => {
  const { chatbotId } = useParams();

  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [editingInterview, setEditingInterview] = useState(null);
  const [feedbackForm, setFeedbackForm] = useState(feedbackFormInitial);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingInterview, setDeletingInterview] = useState(null);
  const [error, setError] = useState(null);

  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);

  useEffect(() => {
    const checkPermissions = async () => {
      const view = await hasPermission('hrm', 'view');
      const modify = await hasPermission('hrm', 'add|edit');
      setCanView(view);
      setCanModify(modify);
    };
    checkPermissions();
  }, []);

  const filters = {
    page: page + 1,
    per_page: rowsPerPage,
    ...(statusFilter && { status: statusFilter }),
    ...(dateFrom && { date_from: dateFrom }),
    ...(dateTo && { date_to: dateTo }),
  };

  const { data: interviewsData, isLoading: loading, refetch: refetchInterviews } = useInterviews(chatbotId, filters, { enabled: !!chatbotId && canView });
  const { data: applicantsData } = useApplicants(chatbotId, { per_page: 100 }, { enabled: !!chatbotId && canView });
  const { data: jobsData } = useJobs(chatbotId, { per_page: 100 }, { enabled: !!chatbotId && canView });

  const interviews = interviewsData?.data || [];
  const totalCount = interviewsData?.pagination?.total || 0;
  const applicants = applicantsData?.data || [];
  const jobs = jobsData?.data || [];

  const createInterview = useCreateInterview(chatbotId);
  const updateInterview = useUpdateInterview(chatbotId);

  const saving = createInterview.isPending || updateInterview.isPending;

  const handleOpenDialog = () => {
    setFormData(initialFormData);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData(initialFormData);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...formData,
        applicant_id: Number(formData.applicant_id),
        job_id: formData.job_id ? Number(formData.job_id) : null,
        duration_minutes: Number(formData.duration_minutes),
      };
      await createInterview.mutateAsync(payload);
      handleCloseDialog();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create interview');
    }
  };

  const handleOpenFeedbackDialog = (interview) => {
    setEditingInterview(interview);
    setFeedbackForm({
      score: interview.score || '',
      feedback: interview.feedback || '',
      recommendation: interview.recommendation || '',
    });
    setFeedbackDialogOpen(true);
  };

  const handleCloseFeedbackDialog = () => {
    setFeedbackDialogOpen(false);
    setEditingInterview(null);
    setFeedbackForm(feedbackFormInitial);
  };

  const handleFeedbackFormChange = (field, value) => {
    setFeedbackForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveFeedback = async () => {
    try {
      const payload = {
        id: editingInterview.id,
        score: feedbackForm.score ? Number(feedbackForm.score) : null,
        feedback: feedbackForm.feedback,
        recommendation: feedbackForm.recommendation,
        status: 'COMPLETED',
      };
      await updateInterview.mutateAsync(payload);
      handleCloseFeedbackDialog();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save feedback');
    }
  };

  const handleCancelInterview = async (interview) => {
    try {
      await updateInterview.mutateAsync({
        id: interview.id,
        status: 'CANCELLED',
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to cancel interview');
    }
  };

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/api/hrm/interviews/${deletingInterview.id}`, {
        params: { chatbot_id: chatbotId },
      });
      setDeleteConfirmOpen(false);
      setDeletingInterview(null);
      refetchInterviews();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete interview');
    }
  };

  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">You do not have permission to view the HRM module.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Interviews
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Tooltip title="Refresh">
            <IconButton onClick={() => refetchInterviews()} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          {canModify && (
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleOpenDialog}>
              Schedule Interview
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          >
            <MenuItem value="">All</MenuItem>
            {INTERVIEW_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="From Date"
          type="date"
          value={dateFrom}
          onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          label="To Date"
          type="date"
          value={dateTo}
          onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      {/* Table */}
      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Applicant Name</TableCell>
                  <TableCell>Job Title</TableCell>
                  <TableCell>Interview Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Interviewer</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Score</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {interviews.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No interviews found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  interviews.map((interview) => (
                    <TableRow key={interview.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {interview.applicant_name || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>{interview.job_title || '-'}</TableCell>
                      <TableCell>
                        {interview.interview_date
                          ? new Date(interview.interview_date).toLocaleString()
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {interview.interview_type
                          ? formatEnumLabel(interview.interview_type)
                          : '-'}
                      </TableCell>
                      <TableCell>{interview.interviewer_name || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={interview.status}
                          size="small"
                          color={INTERVIEW_STATUS_COLORS[interview.status] || 'default'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {interview.score != null ? interview.score : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {canModify && (
                          <>
                            {interview.status === 'SCHEDULED' && (
                              <>
                                <Tooltip title="Add Feedback / Complete">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleOpenFeedbackDialog(interview)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Cancel">
                                  <Button
                                    size="small"
                                    color="error"
                                    onClick={() => handleCancelInterview(interview)}
                                    sx={{ minWidth: 'auto', textTransform: 'none', ml: 0.5 }}
                                  >
                                    Cancel
                                  </Button>
                                </Tooltip>
                              </>
                            )}
                            {interview.status === 'COMPLETED' && (
                              <Tooltip title="Edit Feedback">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleOpenFeedbackDialog(interview)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setDeletingInterview(interview);
                                  setDeleteConfirmOpen(true);
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </>
        )}
      </TableContainer>

      {/* Create Interview Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Schedule Interview</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Applicant</InputLabel>
                <Select
                  value={formData.applicant_id}
                  label="Applicant"
                  onChange={(e) => handleFormChange('applicant_id', e.target.value)}
                >
                  {applicants.map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.first_name} {a.last_name} ({a.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Job</InputLabel>
                <Select
                  value={formData.job_id}
                  label="Job"
                  onChange={(e) => handleFormChange('job_id', e.target.value)}
                >
                  <MenuItem value="">None</MenuItem>
                  {jobs.map((j) => (
                    <MenuItem key={j.id} value={j.id}>{j.title}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Interview Date & Time"
                type="datetime-local"
                value={formData.interview_date}
                onChange={(e) => handleFormChange('interview_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Duration (minutes)"
                type="number"
                value={formData.duration_minutes}
                onChange={(e) => handleFormChange('duration_minutes', e.target.value)}
                inputProps={{ min: 15, step: 15 }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Interview Type</InputLabel>
                <Select
                  value={formData.interview_type}
                  label="Interview Type"
                  onChange={(e) => handleFormChange('interview_type', e.target.value)}
                >
                  {INTERVIEW_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>{formatEnumLabel(t)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Location"
                value={formData.location}
                onChange={(e) => handleFormChange('location', e.target.value)}
                placeholder="Office address or room"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Meeting Link"
                value={formData.meeting_link}
                onChange={(e) => handleFormChange('meeting_link', e.target.value)}
                placeholder="Zoom/Meet link"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Interviewer Name"
                value={formData.interviewer_name}
                onChange={(e) => handleFormChange('interviewer_name', e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !formData.applicant_id || !formData.interview_date}
          >
            {saving ? <CircularProgress size={24} /> : 'Schedule'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feedback Dialog */}
      <Dialog open={feedbackDialogOpen} onClose={handleCloseFeedbackDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingInterview?.status === 'COMPLETED' ? 'Edit Feedback' : 'Complete Interview & Add Feedback'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Score (1-10)"
                type="number"
                value={feedbackForm.score}
                onChange={(e) => handleFeedbackFormChange('score', e.target.value)}
                inputProps={{ min: 1, max: 10 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Feedback"
                value={feedbackForm.feedback}
                onChange={(e) => handleFeedbackFormChange('feedback', e.target.value)}
                multiline
                rows={4}
                placeholder="Provide detailed feedback about the interview..."
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Recommendation</InputLabel>
                <Select
                  value={feedbackForm.recommendation}
                  label="Recommendation"
                  onChange={(e) => handleFeedbackFormChange('recommendation', e.target.value)}
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="STRONG_YES">Strong Yes</MenuItem>
                  <MenuItem value="YES">Yes</MenuItem>
                  <MenuItem value="MAYBE">Maybe</MenuItem>
                  <MenuItem value="NO">No</MenuItem>
                  <MenuItem value="STRONG_NO">Strong No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFeedbackDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveFeedback}
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} /> : 'Save Feedback'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Interview</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this interview? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InterviewList;
